/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  useGetSubscriptionPlansQuery,
  useGetCurrentSubscriptionQuery,
  useSelectPlanMutation,
} from '../../features/auth/authApi';
import { SubscriptionPlan } from '../../types/subscription';
import { formatCurrency } from '../../utils/currency';
import PlanChangeConfirmationDialog from './PlanChangeConfirmationDialog';
import AddPaymentMethodModal from '../pages/Billing/components/AddPaymentMethodModal';
import { SecurePaymentProvider } from '../payment/providers/SecurePaymentProvider';
import DowngradeViolationDialog from './DowngradeViolationDialog';

/**
 * Props for the PlanChangeDialog component.
 */
interface PlanChangeDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;
  /**
   * Callback when the dialog should be closed.
   */
  onClose: () => void;
  /**
   * Callback when a plan change is successful.
   */
  onSuccess?: () => void;
}

/**
 * Dialog component for changing subscription plans.
 * Displays available plans and allows users to select a new plan.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The plan change dialog component
 *
 * @example
 * return (
 *   <PlanChangeDialog
 *     open={isDialogOpen}
 *     onClose={() => setIsDialogOpen(false)}
 *     onSuccess={() => {
 *       setIsDialogOpen(false);
 *       // Show success message
 *     }}
 *   />
 * );
 */
const PlanChangeDialog: React.FC<PlanChangeDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('billing');
  // Get subscription data
  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
  } = useGetSubscriptionPlansQuery();
  const { data: currentSubscription } = useGetCurrentSubscriptionQuery();
  const [selectPlan, { isLoading: isChangingPlan, error: changeError }] =
    useSelectPlanMutation();

  // Handle different possible data structures for plans
  const getPlansArray = (): SubscriptionPlan[] => {
    if (!plans) return [];
    if (Array.isArray(plans)) return plans;
    if ((plans as any).results && Array.isArray((plans as any).results))
      return (plans as any).results;
    if ((plans as any).plans && Array.isArray((plans as any).plans))
      return (plans as any).plans;
    if ((plans as any).data && Array.isArray((plans as any).data))
      return (plans as any).data;
    console.warn('Unexpected plans data structure:', plans);
    return [];
  };

  const plansArray = getPlansArray();

  // Dialog state management
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [violationDialogOpen, setViolationDialogOpen] = useState(false);
  const [violations, setViolations] = useState<string[]>([]);
  const [hasShownViolationDialog, setHasShownViolationDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );

  // Handle plan selection - show confirmation first
  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setConfirmationOpen(true);
    // Reset violation dialog flag for fresh attempts
    setHasShownViolationDialog(false);
  };

  // Handle confirmed plan change
  const handleConfirmPlanChange = async () => {
    if (!selectedPlan) return;

    try {
      // Determine if this is an upgrade or downgrade
      // During beta, use plan hierarchy rather than price since many plans are $0
      const planHierarchy = ['free', 'starter', 'growth', 'pro'];
      const currentPlanIndex = planHierarchy.indexOf(
        currentSubscription?.plan?.slug || 'free'
      );
      const selectedPlanIndex = planHierarchy.indexOf(selectedPlan.slug);

      // If plan hierarchy doesn't work, fall back to price comparison
      let isUpgrade: boolean;
      if (currentPlanIndex >= 0 && selectedPlanIndex >= 0) {
        isUpgrade = selectedPlanIndex > currentPlanIndex;
      } else {
        const currentPlanPrice = currentSubscription?.plan?.price_monthly || 0;
        const selectedPlanPrice = selectedPlan.price_monthly || 0;
        isUpgrade = selectedPlanPrice > currentPlanPrice;
      }

      console.log('Plan change details:', {
        currentPlan: currentSubscription?.plan?.name,
        currentPlanSlug: currentSubscription?.plan?.slug,
        currentPlanIndex,
        selectedPlan: selectedPlan.name,
        selectedPlanSlug: selectedPlan.slug,
        selectedPlanIndex,
        isUpgrade,
      });

      await selectPlan({
        plan_slug: selectedPlan.slug,
        billing_cycle: 'monthly', // Default to monthly for beta
        isUpgrade,
      }).unwrap();

      // Close confirmation dialog
      setConfirmationOpen(false);
      setSelectedPlan(null);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to change plan:', error);
      console.error('Error data:', error?.data);
      console.error('Error message:', error?.data?.error || error?.message);

      // Check for downgrade violations
      if (
        error?.data?.downgrade_violations &&
        Array.isArray(error.data.downgrade_violations)
      ) {
        setViolations(error.data.downgrade_violations);
        setConfirmationOpen(false);
        setViolationDialogOpen(true);
        setHasShownViolationDialog(true);
        return; // Exit early to prevent further error handling
      }

      // Check if this is a payment method validation error
      const errorMessage = error?.data?.error || error?.message || '';
      const isPaymentMethodError =
        errorMessage.includes('payment method is required') ||
        errorMessage.includes('payment method') ||
        errorMessage.includes('billing readiness');

      if (isPaymentMethodError) {
        // Close confirmation dialog and show payment method modal
        setConfirmationOpen(false);
        setPaymentMethodModalOpen(true);
      }
      // Other errors will be handled by the mutation error state
    }
  };

  // Handle confirmation cancel
  const handleCancelConfirmation = () => {
    setConfirmationOpen(false);
    setSelectedPlan(null);
  };

  // Handle payment method modal close
  const handlePaymentMethodModalClose = () => {
    setPaymentMethodModalOpen(false);
    // Keep selectedPlan so user can try again after adding payment method
  };

  // Handle payment method success
  const handlePaymentMethodSuccess = () => {
    setPaymentMethodModalOpen(false);

    // Automatically retry the plan change after payment method is added
    if (selectedPlan) {
      setConfirmationOpen(true);
    }
  };

  // Handle violation dialog close
  const handleViolationDialogClose = () => {
    setViolationDialogOpen(false);
    setViolations([]);
    setSelectedPlan(null);
  };

  // Get current plan ID for comparison
  const currentPlanId = currentSubscription?.plan?.id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="plan-change-dialog-title"
    >
      <DialogTitle id="plan-change-dialog-title">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{t('plans.changePlan')}</Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto' }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {plansLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t('usage.loadingPlans')}
            </Typography>
          </Box>
        )}
        {!plansLoading && plansArray.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('usage.noPlansAvailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('usage.tryAgainLater')}
            </Typography>
          </Box>
        )}
        {!plansLoading && plansArray.length > 0 && (
          <>
            {/* Beta Notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{t('plans.betaFree')}</strong>
              </Typography>
            </Alert>

            {/* Error Display - Only show if we haven't already shown a violation dialog */}
            {(changeError || plansError) && !hasShownViolationDialog && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  {changeError
                    ? t('plans.changeError')
                    : t('usage.loadingError')}
                </Typography>
              </Alert>
            )}

            {/* Current Plan Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('plans.currentPlan')}:{' '}
                <strong>
                  {currentSubscription?.plan?.name || t('plans.free')}
                </strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('plans.selectPlan')}
              </Typography>
            </Box>

            {/* Available Plans */}
            <Box sx={{ display: 'grid', gap: 2 }}>
              {plansArray.map((plan: SubscriptionPlan) => {
                const isCurrentPlan = plan.id === currentPlanId;

                return (
                  <Card
                    key={plan.id}
                    sx={{
                      border: isCurrentPlan ? 2 : 1,
                      borderColor: isCurrentPlan ? 'primary.main' : 'divider',
                      position: 'relative',
                      opacity: isChangingPlan ? 0.7 : 1,
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6">{plan.name}</Typography>
                            {isCurrentPlan && (
                              <Chip
                                label={t('plans.current')}
                                color="success"
                                size="small"
                              />
                            )}
                          </Box>

                          <Typography variant="h4" color="primary" gutterBottom>
                            {plan.is_beta_free
                              ? '$0.00'
                              : formatCurrency(plan.price_monthly)}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              /month
                            </Typography>
                          </Typography>

                          {plan.is_beta_free && (
                            <Typography variant="body2" color="text.secondary">
                              {t('plans.betaFree')}
                            </Typography>
                          )}
                        </Box>

                        <Button
                          variant={isCurrentPlan ? 'outlined' : 'contained'}
                          color="primary"
                          disabled={
                            isCurrentPlan || isChangingPlan || confirmationOpen
                          }
                          onClick={() => handlePlanSelect(plan)}
                        >
                          {isCurrentPlan
                            ? t('plans.currentPlan')
                            : t('plans.selectPlan')}
                        </Button>
                      </Box>

                      {/* Plan Features */}
                      <Typography variant="subtitle2" gutterBottom>
                        {t('features.features')}:
                      </Typography>
                      <List dense>
                        <ListItem disablePadding>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              plan.max_owned_studios === 0
                                ? t('features.unlimitedStudios')
                                : t('features.upToStudios', {
                                    count: plan.max_owned_studios,
                                  })
                            }
                          />
                        </ListItem>
                        <ListItem disablePadding>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              plan.max_users_per_studio === 0
                                ? t('features.unlimitedUsers')
                                : t('features.upToUsers', {
                                    count: plan.max_users_per_studio,
                                  })
                            }
                          />
                        </ListItem>
                        {(plan.features || []).map((feature: string) => (
                          <ListItem key={feature} disablePadding>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                              />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isChangingPlan}>
          Cancel
        </Button>
      </DialogActions>

      {/* Plan Change Confirmation Dialog */}
      <PlanChangeConfirmationDialog
        open={confirmationOpen}
        currentPlan={currentSubscription?.plan || null}
        newPlan={selectedPlan}
        onConfirm={handleConfirmPlanChange}
        onCancel={handleCancelConfirmation}
        isLoading={isChangingPlan}
      />

      {/* Add Payment Method Modal */}
      <SecurePaymentProvider>
        <AddPaymentMethodModal
          open={paymentMethodModalOpen}
          onClose={handlePaymentMethodModalClose}
          onSuccess={handlePaymentMethodSuccess}
        />
      </SecurePaymentProvider>

      {/* Downgrade Violation Dialog */}
      <DowngradeViolationDialog
        open={violationDialogOpen}
        onClose={handleViolationDialogClose}
        targetPlan={selectedPlan}
        violations={violations}
      />
    </Dialog>
  );
};

export default PlanChangeDialog;
